/**
 * BeginProductionDialog — Sprint 5 · Project Symphony
 *
 * The "Begin Campaign Production" confirmation. Premium video render
 * approval is OPT-IN — the checkboxes for HeyGen (avatar video) and
 * Runway (generative video) are unticked by default.
 *
 * Copy is business-first. The dialog never mentions provider names.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { beginProduction } from "../api";

export function BeginProductionDialog({
  open,
  onOpenChange,
  blueprintId,
  totalAssets,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blueprintId: string;
  totalAssets: number;
}): JSX.Element {
  const [avatarVideo, setAvatarVideo] = useState(false);
  const [generativeVideo, setGenerativeVideo] = useState(false);
  const qc = useQueryClient();

  const begin = useMutation({
    mutationFn: () =>
      beginProduction(blueprintId, {
        heygen: avatarVideo,
        runway: generativeVideo,
      }),
    onSuccess: (state) => {
      qc.setQueryData(["campaign-generation", blueprintId], state);
      qc.invalidateQueries({ queryKey: ["campaign-room", blueprintId] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="begin-production-dialog">
        <DialogHeader>
          <DialogTitle>Begin Campaign Production</DialogTitle>
          <DialogDescription>
            Your Marketing Department is ready to start work. Once you begin,
            each department will take on its part of the campaign.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border border-border p-4 bg-muted/30">
            <p className="text-sm font-medium mb-1">
              {totalAssets} assets planned
            </p>
            <p className="text-xs text-muted-foreground">
              The Content Team will queue the work. The Creative Studio will
              produce each asset. The Publishing Team will prepare the
              schedule for your review.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Premium video approval
            </p>
            <p className="text-sm text-muted-foreground">
              Producing video takes noticeably more capacity. Approve one
              short video per format if you&apos;d like the Creative Studio
              to include video in this campaign.
            </p>
            <div className="flex items-center gap-2">
              <Checkbox
                id="approve-avatar-video"
                checked={avatarVideo}
                onCheckedChange={(v) => setAvatarVideo(v === true)}
                data-testid="approve-avatar-video"
              />
              <label htmlFor="approve-avatar-video" className="text-sm cursor-pointer">
                Approve one short avatar-led video
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="approve-generative-video"
                checked={generativeVideo}
                onCheckedChange={(v) => setGenerativeVideo(v === true)}
                data-testid="approve-generative-video"
              />
              <label htmlFor="approve-generative-video" className="text-sm cursor-pointer">
                Approve one short generative video
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={begin.isPending}
            data-testid="begin-production-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => begin.mutate()}
            disabled={begin.isPending}
            data-testid="begin-production-confirm"
          >
            {begin.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                Starting…
              </>
            ) : (
              "Begin Campaign Production"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
